"use client";

import { useEffect, useRef } from "react";

/* Vídeo decorativo mudo em loop que não disputa banda com o carregamento.
   A capa (poster, WebP leve) aparece na hora sem competir com o texto; o vídeo só
   começa a baixar depois do onload (acima da dobra) ou quando entra na tela. */
export default function AutoVideo({
  src,
  poster,
  className,
  acimaDaDobra = false,
}: {
  src: string;
  poster: string;
  className?: string;
  acimaDaDobra?: boolean;
}) {
  const ref = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const v = ref.current;
    if (!v) return;
    const tocar = () => {
      if (!v.src) v.src = src;
      v.play().catch(() => {});
    };
    if (acimaDaDobra) {
      const iniciar = () => setTimeout(tocar, 300);
      if (document.readyState === "complete") iniciar();
      else addEventListener("load", iniciar, { once: true });
      const volta = () => document.visibilityState === "visible" && v.src && v.play().catch(() => {});
      document.addEventListener("visibilitychange", volta);
      return () => {
        removeEventListener("load", iniciar);
        document.removeEventListener("visibilitychange", volta);
      };
    }
    const perto = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          v.poster = poster;
          perto.disconnect();
        }
      },
      { rootMargin: "800px 0px" }
    );
    const io = new IntersectionObserver(
      ([e]) => (e.isIntersecting ? tocar() : v.pause()),
      { rootMargin: "200px 0px" }
    );
    perto.observe(v);
    io.observe(v);
    return () => {
      perto.disconnect();
      io.disconnect();
    };
  }, [src, poster, acimaDaDobra]);

  return <video ref={ref} poster={acimaDaDobra ? poster : undefined} muted loop playsInline preload="none" aria-hidden className={className} />;
}
